import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { newId, parseNum } from "@/lib/format";

const BANDS = { bt: [[0, 300], [300, 600], [600, 2000], [2000, 4000], [4000, 10000], [10000, null]], length: [[0, 10], [10, 15], [15, 25], [25, 50], [50, null]] };
const colsFor = (b) => (b === "bt" ? { from: "Fra BT", to: "Til BT", rate: "Sats kr/BT" } : { from: "Fra meter", to: "Til meter", rate: "Sats kr/meter" });
const ISPS = { isps: { enabled: false, rate: 0.3 }, waste: { enabled: false, rate: 0.52, min: 217 } };

const Choice = ({ value, cur, onPick, title, hint, testId }) => (
  <button type="button" onClick={() => onPick(value)} data-testid={testId}
    className={`text-left rounded-md border px-3 py-2 transition-colors ${cur === value ? "border-sky-600 bg-sky-50 ring-1 ring-sky-600" : "border-slate-200 hover:border-sky-300"}`}>
    <span className="block text-sm font-medium text-slate-900">{title}</span>
    <span className="block text-xs text-slate-500">{hint}</span>
  </button>
);

export const NewTableDialog = ({ open, onOpenChange, onCreate }) => {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (open) { setF({ name: "", target: "own", basis: "bt", per_day: false, min: "0", ...colsFor("bt") }); }
  }, [open]);
  if (!f) return null;
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const basis = f.target === "own" ? f.basis : "bt";
  const pickBasis = (b) => set({ basis: b, ...colsFor(b) });
  const pickTarget = (t) => set({ target: t, ...(t === "line" ? colsFor("bt") : colsFor(f.basis)) });
  const min = parseNum(f.min);
  const ok = f.name.trim() && f.from.trim() && f.to.trim() && f.rate.trim() && min !== null && !Number.isNaN(min) && min >= 0;

  const create = () => {
    const table = { id: newId(), name: f.name.trim().slice(0, 60), min_price: min, per_day: f.per_day, method: "progressive",
      cols: { from: f.from.trim(), to: f.to.trim(), rate: f.rate.trim() },
      tiers: BANDS[basis].map(([a, b]) => ({ from_bt: a, to_bt: b, rate: 0 })) };
    onCreate(f.target, f.target === "own" ? { id: newId(), basis, table, extras: ISPS } : table);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="new-table-dialog">
        <DialogHeader>
          <DialogTitle>Ny tabell</DialogTitle>
          <DialogDescription>Velg hvordan tabellen skal brukes. Prisene fyller du inn i tabellen etterpå.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="nt-name">Navn på tabellen</Label>
            <Input id="nt-name" value={f.name} maxLength={60} onChange={(e) => set({ name: e.target.value })} placeholder="f.eks. Slepebåter" data-testid="new-table-name" />
            {f.target === "own" && <p className="text-xs text-slate-500">Navnet vises som eget valg i nedtrekkslisten «Fartøytype».</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Hvor skal tabellen brukes?</Label>
            <div className="grid sm:grid-cols-2 gap-2">
              <Choice value="own" cur={f.target} onPick={pickTarget} title="Eget valg i nedtrekkslisten" hint="Egen fartøytype med egne priser, ISPS og avfallsgebyr" testId="new-table-target-own" />
              <Choice value="line" cur={f.target} onPick={pickTarget} title="Ny avgiftslinje" hint="Legges til summen for generelle fartøy" testId="new-table-target-line" />
            </div>
          </div>
          {f.target === "own" && (
            <div className="space-y-1.5">
              <Label>Prisen regnes ut fra</Label>
              <div className="grid sm:grid-cols-2 gap-2">
                <Choice value="bt" cur={f.basis} onPick={pickBasis} title="Brutto tonnasje (BT)" hint="Brukeren skriver inn BT" testId="new-table-basis-bt" />
                <Choice value="length" cur={f.basis} onPick={pickBasis} title="Lengde i meter" hint="Brukeren skriver inn lengde" testId="new-table-basis-length" />
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Navn på kolonnene</Label>
            <div className="grid grid-cols-3 gap-2">
              {[["from", "Fra"], ["to", "Til"], ["rate", "Sats"]].map(([k, l]) => (
                <Input key={k} aria-label={l} value={f[k]} maxLength={30} onChange={(e) => set({ [k]: e.target.value })} data-testid={`new-table-col-${k}`} />
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="nt-min">Minstepris (kr)</Label>
              <Input id="nt-min" inputMode="decimal" value={f.min} onChange={(e) => set({ min: e.target.value })} data-testid="new-table-min" />
            </div>
            <label className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2 self-end h-10 cursor-pointer">
              <span className="text-sm text-slate-700">Ganges med antall døgn</span>
              <Switch checked={f.per_day} onCheckedChange={(v) => set({ per_day: v })} data-testid="new-table-per-day" />
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="new-table-cancel">Avbryt</Button>
          <Button onClick={create} disabled={!ok} data-testid="new-table-create">Opprett tabell</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
